import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface GoogleVeo30FastGenerate001Params {
  /**
   * 视频生成提示词，长度不超过2000字符
   */
  prompt: string;
  /**
   * 输入图像，用于I2V模式，支持URL、base64数据或包含bytesBase64Encoded/gcsUri的对象
   */
  image?: string | any;
  /**
   * 负向提示词
   */
  negativePrompt?: string;
  /**
   * 视频时长（秒），支持4、6或8秒，默认6秒（快速生成使用较短时长）
   */
  durationSeconds?: number;
  /**
   * 视频宽高比，支持: 9:16, 16:9，默认16:9
   */
  aspectRatio?: string;
  /**
   * 视频分辨率，支持: 720p, 1080p，默认720p（快速生成使用较低分辨率）
   */
  resolution?: string;
  /**
   * 压缩质量，支持: optimized（优化）, lossless（无损），默认optimized（快速生成使用优化压缩）
   */
  compressionQuality?: string;
  /**
   * 是否增强提示词，默认true
   */
  enhancePrompt?: boolean;
  /**
   * 是否生成音频（Veo 3.0新增功能）
   */
  generateAudio?: boolean;
  /**
   * 人物生成设置，支持: dont_allow（不允许）, allow_adult（允许成年人），默认allow_adult
   */
  personGeneration?: string;
  /**
   * 生成样本数量，范围1-4，默认1（快速生成最多限制2个）
   */
  sampleCount?: number;
  /**
   * 随机种子，范围0-4294967295
   */
  seed?: number;
  /**
   * 存储URI，用于指定GCS存储位置，默认返回base64编码
   */
  storageUri?: string;
}

export interface GoogleVeo30FastGenerate001Resource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * google_veo_30_fast_generate_001
 *
 * Google Veo 3.0 Fast Generate 001 快速视频生成接口
 * 
 * Model: veo-3.0-fast-generate-001 (固定模型)
 * Features: 快速视频生成功能，支持文本到视频(T2V)和图像到视频(I2V)
 * Duration: 4、6或8秒（默认6秒，针对快速生成优化）
 * Resolution: 720p、1080p（默认720p，快速生成使用较低分辨率）
 * Aspect Ratio: 9:16、16:9（默认16:9）
 * Sample Count: 1-4个（默认1个，快速生成推荐1-2个）
 * Advanced: 支持提示词增强、音频生成、人物生成控制、压缩质量设置（优化/无损）
 * 
 *
 * @example
 * ```typescript
 * import { initSeacloud, googleVeo30FastGenerate001 } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await googleVeo30FastGenerate001({
 *   // 参数见 GoogleVeo30FastGenerate001Params 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 GoogleVeo30FastGenerate001Params 接口定义
 * @returns 生成的资源列表
 */
export async function googleVeo30FastGenerate001(
  params: GoogleVeo30FastGenerate001Params,
): Promise<GoogleVeo30FastGenerate001Resource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'google_veo_30_fast_generate_001',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: GoogleVeo30FastGenerate001Resource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
